import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import Header from '../components/Header';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [analytics, setAnalytics] = useState({
    support: {
      totalTickets: 0,
      openTickets: 0,
      resolvedTickets: 0,
      avgResponseTime: 0,
      satisfactionScore: 0,
      categoryBreakdown: {},
      priorityBreakdown: {},
      dailyTickets: []
    },
    users: {
      totalUsers: 0,
      activeUsers: 0,
      newUsers: 0,
      userGrowth: [],
      userEngagement: 0
    },
    teams: {
      totalTeams: 0,
      activeTeams: 0,
      newTeams: 0,
      avgTeamSize: 0,
      teamGrowth: []
    },
    games: {
      totalGames: 0,
      activeGames: 0,
      completedGames: 0,
      avgRating: 0,
      gamePerformance: []
    },
    system: {
      uptime: 99.9,
      responseTime: 0.2,
      errorRate: 0.1,
      performance: []
    }
  });
  const navigate = useNavigate();

  const calculateSupportMetrics = useCallback((tickets) => {
    const now = new Date();
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = subDays(now, daysAgo);

    const filteredTickets = tickets.filter(ticket => 
      ticket.createdAt?.toDate?.() ? ticket.createdAt.toDate() >= startDate : true
    );

    const totalTickets = filteredTickets.length;
    const openTickets = filteredTickets.filter(t => t.status === 'open').length;
    const resolvedTickets = filteredTickets.filter(t => t.status === 'resolved').length;

    // Calculate average response time
    const ticketsWithResponses = filteredTickets.filter(t => t.responses && t.responses.length > 0);
    const avgResponseTime = ticketsWithResponses.length > 0 
      ? ticketsWithResponses.reduce((total, ticket) => {
          const firstResponse = ticket.responses[0];
          const responseTime = firstResponse.timestamp?.toDate?.() 
            ? (firstResponse.timestamp.toDate() - ticket.createdAt.toDate()) / (1000 * 60 * 60) // hours
            : 0;
          return total + responseTime;
        }, 0) / ticketsWithResponses.length
      : 0;

    // Category breakdown
    const categoryBreakdown = {};
    filteredTickets.forEach(ticket => {
      const category = ticket.category || 'other';
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + 1;
    });

    // Priority breakdown
    const priorityBreakdown = {};
    filteredTickets.forEach(ticket => {
      const priority = ticket.priority || 'medium';
      priorityBreakdown[priority] = (priorityBreakdown[priority] || 0) + 1;
    });

    // Daily tickets
    const dailyTickets = [];
    for (let i = 0; i < daysAgo; i++) {
      const date = subDays(now, i);
      const dayTickets = filteredTickets.filter(ticket => {
        const ticketDate = ticket.createdAt?.toDate?.() ? ticket.createdAt.toDate() : new Date();
        return ticketDate >= startOfDay(date) && ticketDate <= endOfDay(date);
      }).length;
      dailyTickets.unshift({ date: format(date, 'MMM d'), count: dayTickets });
    }

    return {
      totalTickets,
      openTickets,
      resolvedTickets,
      avgResponseTime: Math.round(avgResponseTime * 10) / 10,
      satisfactionScore: 4.2, // Placeholder
      categoryBreakdown,
      priorityBreakdown,
      dailyTickets
    };
  }, [timeRange]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch support tickets
      const ticketsQuery = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));
      const ticketsSnapshot = await getDocs(ticketsQuery);
      const tickets = ticketsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate support metrics
      const supportMetrics = calculateSupportMetrics(tickets);
      
      // Fetch other data (real data now)
      const userMetrics = await calculateUserMetrics();
      const teamMetrics = await calculateTeamMetrics();
      const gameMetrics = await calculateGameMetrics();
      const systemMetrics = calculateSystemMetrics();

      setAnalytics({
        support: supportMetrics,
        users: userMetrics,
        teams: teamMetrics,
        games: gameMetrics,
        system: systemMetrics
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [calculateSupportMetrics]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const calculateUserMetrics = async () => {
    try {
      // Fetch real user data
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const totalUsers = users.length;
      const teams = users.filter(user => user.role === 'team' || user.role === 'team_captain');
      // eslint-disable-next-line no-unused-vars
      const totalTeams = teams.length;
      
      // Calculate active users (users active in last 7 days)
      const sevenDaysAgo = subDays(new Date(), 7);
      const activeUsers = users.filter(user => {
        const lastActive = user.lastActive?.toDate?.();
        return lastActive && lastActive >= sevenDaysAgo;
      }).length;
      
      // Calculate new users in last 7 days
      const newUsers = users.filter(user => {
        const createdAt = user.createdAt?.toDate?.();
        return createdAt && createdAt >= sevenDaysAgo;
      }).length;
      
      const userEngagement = totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0;
      
      // Calculate hosts
      const hosts = users.filter(user => user.role === 'host');
      const totalHosts = hosts.length;
      
      return {
        totalUsers,
        totalHosts,
        activeUsers,
        newUsers,
        userGrowth: [
          { date: 'Jan', count: totalUsers },
          { date: 'Feb', count: totalUsers },
          { date: 'Mar', count: totalUsers }
        ],
        userEngagement
      };
    } catch (error) {
      console.error('Error fetching user metrics:', error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        newUsers: 0,
        userGrowth: [],
        userEngagement: 0
      };
    }
  };

  const calculateTeamMetrics = async () => {
    try {
      // Teams are stored in users collection with role 'team' or 'team_captain'
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const teams = users.filter(user => user.role === 'team' || user.role === 'team_captain');
      

      
      const totalTeams = teams.length;
      
      // Calculate active teams (teams active in last 7 days)
      const sevenDaysAgo = subDays(new Date(), 7);
      const activeTeams = teams.filter(team => {
        const lastActive = team.lastActive?.toDate?.();
        return lastActive && lastActive >= sevenDaysAgo;
      }).length;
      
      // Calculate new teams in last 7 days
      const newTeams = teams.filter(team => {
        const createdAt = team.createdAt?.toDate?.();
        return createdAt && createdAt >= sevenDaysAgo;
      }).length;
      
      return {
        totalTeams,
        activeTeams,
        newTeams,
        avgTeamSize: totalTeams > 0 ? Math.round((totalTeams / totalTeams) * 10) / 10 : 0,
        teamGrowth: [
          { date: 'Jan', count: totalTeams },
          { date: 'Feb', count: totalTeams },
          { date: 'Mar', count: totalTeams }
        ]
      };
    } catch (error) {
      console.error('Error fetching team metrics:', error);
      return {
        totalTeams: 0,
        activeTeams: 0,
        newTeams: 0,
        avgTeamSize: 0,
        teamGrowth: []
      };
    }
  };

  const calculateGameMetrics = async () => {
    try {
      // Fetch real game data
      const gamesSnapshot = await getDocs(collection(db, 'games'));
      const games = gamesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const totalGames = games.length;
      
      // Calculate active games (games created in last 7 days)
      const sevenDaysAgo = subDays(new Date(), 7);
      const activeGames = games.filter(game => {
        const createdAt = game.createdAt?.toDate?.();
        return createdAt && createdAt >= sevenDaysAgo;
      }).length;
      
      const completedGames = totalGames - activeGames;
      
      return {
        totalGames,
        activeGames,
        completedGames,
        avgRating: 4.6, // Placeholder - would calculate from ratings
        gamePerformance: [
          { date: 'Jan', count: totalGames },
          { date: 'Feb', count: totalGames },
          { date: 'Mar', count: totalGames }
        ]
      };
    } catch (error) {
      console.error('Error fetching game metrics:', error);
      return {
        totalGames: 0,
        activeGames: 0,
        completedGames: 0,
        avgRating: 0,
        gamePerformance: []
      };
    }
  };

  const calculateSystemMetrics = () => {
    return {
      uptime: 99.9,
      responseTime: 0.2,
      errorRate: 0.1,
      performance: [
        { date: 'Jan', uptime: 99.8 },
        { date: 'Feb', uptime: 99.9 },
        { date: 'Mar', uptime: 99.9 }
      ]
    };
  };

  const getMetricColor = (metric, value) => {
    if (metric === 'uptime' || metric === 'satisfaction' || metric === 'engagement') {
      return value >= 90 ? 'text-green-400' : value >= 70 ? 'text-yellow-400' : 'text-red-400';
    }
    if (metric === 'responseTime' || metric === 'errorRate') {
      return value <= 1 ? 'text-green-400' : value <= 5 ? 'text-yellow-400' : 'text-red-400';
    }
    return 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-green-300 hover:text-green-400"
            >
              ← Back to Admin Dashboard
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() => navigate('/admin/moderation')}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Moderation
              </button>
              <button
                onClick={() => navigate('/admin/marketplace')}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Marketplace
              </button>
              <button
                onClick={() => navigate('/admin/ads')}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Ads & Slides
              </button>
              <button
                onClick={() => navigate('/admin/security')}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
              >
                Security
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Analytics Dashboard</h1>
              <p className="text-gray-300">Comprehensive insights into platform performance</p>
            </div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-700 rounded-md focus:ring-2 focus:ring-green-400 bg-gray-800 text-white"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>

        {/* Support Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Support Performance</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{analytics.support.totalTickets}</div>
                <div className="text-sm text-gray-300">Total Tickets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-400">{analytics.support.openTickets}</div>
                <div className="text-sm text-gray-300">Open Tickets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{analytics.support.resolvedTickets}</div>
                <div className="text-sm text-gray-300">Resolved</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${getMetricColor('responseTime', analytics.support.avgResponseTime)}`}>
                  {analytics.support.avgResponseTime}h
                </div>
                <div className="text-sm text-gray-300">Avg Response</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">User Engagement</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{analytics.users.totalUsers}</div>
                <div className="text-sm text-gray-300">Total Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{analytics.users.activeUsers}</div>
                <div className="text-sm text-gray-300">Active Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{analytics.teams.totalTeams}</div>
                <div className="text-sm text-gray-300">Total Teams</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">{analytics.users.totalHosts}</div>
                <div className="text-sm text-gray-300">Total Hosts</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${getMetricColor('engagement', analytics.users.userEngagement)}`}>
                  {analytics.users.userEngagement}%
                </div>
                <div className="text-sm text-gray-300">Engagement</div>
              </div>
            </div>
          </div>
        </div>

        {/* Category and Priority Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Ticket Categories</h3>
            <div className="space-y-3">
              {Object.entries(analytics.support.categoryBreakdown).map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-300 capitalize">{category}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-600 rounded-full h-2">
                      <div 
                        className="bg-blue-400 h-2 rounded-full" 
                        style={{ width: `${(count / analytics.support.totalTickets) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-300">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Ticket Priorities</h3>
            <div className="space-y-3">
              {Object.entries(analytics.support.priorityBreakdown).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-300 capitalize">{priority}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-600 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          priority === 'urgent' ? 'bg-red-400' :
                          priority === 'high' ? 'bg-orange-400' :
                          priority === 'medium' ? 'bg-yellow-400' : 'bg-green-400'
                        }`}
                        style={{ width: `${(count / analytics.support.totalTickets) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-300">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Daily Trends */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">Daily Ticket Trends</h3>
          <div className="flex items-end justify-between h-32">
            {analytics.support.dailyTickets.map((day, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="text-xs text-gray-300 mb-1">{day.count}</div>
                <div 
                  className="bg-blue-400 rounded-t w-8"
                  style={{ height: `${Math.max((day.count / Math.max(...analytics.support.dailyTickets.map(d => d.count))) * 80, 4)}px` }}
                ></div>
                <div className="text-xs text-gray-400 mt-1">{day.date}</div>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">System Uptime</h3>
            <div className="text-center">
              <div className={`text-3xl font-bold ${getMetricColor('uptime', analytics.system.uptime)}`}>
                {analytics.system.uptime}%
              </div>
              <div className="text-sm text-gray-300 mt-2">Platform Availability</div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Response Time</h3>
            <div className="text-center">
              <div className={`text-3xl font-bold ${getMetricColor('responseTime', analytics.system.responseTime)}`}>
                {analytics.system.responseTime}s
              </div>
              <div className="text-sm text-gray-300 mt-2">Average API Response</div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Error Rate</h3>
            <div className="text-center">
              <div className={`text-3xl font-bold ${getMetricColor('errorRate', analytics.system.errorRate)}`}>
                {analytics.system.errorRate}%
              </div>
              <div className="text-sm text-gray-600 mt-2">System Errors</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics; 